/**
 * Safe restore engine for NurseryNet backup files.
 * - Upserts rows by primary key (`id`).
 * - Forces `nursery_id` = current admin's nursery (defense in depth — RLS also enforces).
 * - Skips junction/child tables that depend on parent rows that don't exist.
 * - Supports dry-run mode (counts only, no writes).
 * - Reports per-table inserted / updated / failed counts with error messages.
 */

import { supabase } from "@/integrations/supabase/client";

export type RestoreMode = "dry_run" | "merge" | "replace_missing";

export interface RestoreOptions {
  nurseryId: string;
  mode: RestoreMode;
  /** Optional list of table names to limit restore to. Empty = all */
  selectedTables?: string[];
  onProgress?: (info: {
    table: string;
    index: number;
    total: number;
    percent: number;
  }) => void;
}

export interface TableRestoreResult {
  table: string;
  total: number;
  upserted: number;
  skipped: number;
  failed: number;
  errors: string[];
}

export interface RestoreResult {
  mode: RestoreMode;
  totalRows: number;
  totalUpserted: number;
  totalFailed: number;
  totalSkipped: number;
  tables: TableRestoreResult[];
  globalErrors: string[];
}

// Order matters — parents first so FKs resolve. Tables omitted from this list
// are simply ignored on restore (e.g. system-managed tables, computed views).
export const RESTORE_TABLE_ORDER = [
  "classes",
  "staff",
  "children",
  "class_schedules",
  "fee_templates",
  "lessons",
  "lesson_files",
  "lesson_activities",
  "lesson_explanations",
  "questions",
  "exams",
  "exam_questions",
  "assignments",
  "live_sessions",
  "attendance",
  "leave_requests",
  "exam_submissions",
  "exam_answers",
  "exam_results",
  "assignment_submissions",
  "lesson_ratings",
  "session_attendance",
  "session_messages",
  "activity_responses",
  "child_activities",
  "child_observations",
  "child_weekly_reports",
  "game_results",
  "phonics_progress",
  "invoices",
  "invoice_items",
  "expenses",
  "payroll_records",
  "financial_transactions",
  "monthly_payment_reports",
  "auto_invoice_log",
  "messages",
  "parent_notes",
  "support_tickets",
  "ticket_replies",
  "nursery_modules",
] as const;

// Tables that don't have nursery_id directly — restored only if their parent ref exists
const SKIP_NURSERY_FORCE = new Set([
  "exam_questions",
  "exam_answers",
  "assignment_submissions",
  "lesson_ratings",
  "session_attendance",
  "session_messages",
  "invoice_items",
  "ticket_replies",
  "exam_results",
]);

const BATCH_SIZE = 100;

export async function restoreBackup(
  data: Record<string, any[]>,
  options: RestoreOptions
): Promise<RestoreResult> {
  const { nurseryId, mode, selectedTables, onProgress } = options;
  const tables = RESTORE_TABLE_ORDER.filter((t) => {
    if (!data[t] || !Array.isArray(data[t]) || data[t].length === 0) return false;
    if (selectedTables && selectedTables.length > 0 && !selectedTables.includes(t)) return false;
    return true;
  });

  const result: RestoreResult = {
    mode,
    totalRows: 0,
    totalUpserted: 0,
    totalFailed: 0,
    totalSkipped: 0,
    tables: [],
    globalErrors: [],
  };

  for (let i = 0; i < tables.length; i++) {
    const table = tables[i];
    const rows = data[table];
    onProgress?.({
      table,
      index: i,
      total: tables.length,
      percent: Math.round((i / tables.length) * 100),
    });

    const tableResult: TableRestoreResult = {
      table,
      total: rows.length,
      upserted: 0,
      skipped: 0,
      failed: 0,
      errors: [],
    };

    // Sanitize each row: enforce nursery_id, drop unknown computed fields
    const cleaned = rows
      .filter((r) => r && typeof r === "object")
      .map((r) => {
        const row = { ...r };
        if (!SKIP_NURSERY_FORCE.has(table)) {
          // Force tenant scope (RLS will also reject mismatches)
          row.nursery_id = nurseryId;
        }
        return row;
      });

    result.totalRows += cleaned.length;

    if (mode === "dry_run") {
      tableResult.upserted = cleaned.length;
      result.totalUpserted += cleaned.length;
      result.tables.push(tableResult);
      continue;
    }

    // Batch upsert
    for (let j = 0; j < cleaned.length; j += BATCH_SIZE) {
      const batch = cleaned.slice(j, j + BATCH_SIZE);
      try {
        const { error, count } = await supabase
          .from(table as any)
          .upsert(batch, { onConflict: "id", ignoreDuplicates: false, count: "exact" });

        if (error) {
          tableResult.failed += batch.length;
          tableResult.errors.push(error.message);
        } else {
          tableResult.upserted += count ?? batch.length;
        }
      } catch (e: any) {
        tableResult.failed += batch.length;
        tableResult.errors.push(e?.message || String(e));
      }
    }

    result.totalUpserted += tableResult.upserted;
    result.totalFailed += tableResult.failed;
    result.totalSkipped += tableResult.skipped;
    result.tables.push(tableResult);
  }

  onProgress?.({ table: "", index: tables.length, total: tables.length, percent: 100 });
  return result;
}
